import { UNDERLINE_STATIC } from '@/components/ui/link-underline';
import { cn } from '@/lib/utils';
import { Badge, type StatusTone } from '@/components/ui/badge';
import type { AttioSync, AttioSyncState } from '@api/orders';
import { useTranslation } from '@hooks/useTranslation';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ordersQueryKeys, retryAttioSync } from '@api/orders';
import { can, CAPABILITIES, type CurrentUser } from '@/react/lib/capabilities';
import { Button } from '@/components/ui/button';
import { RefreshIcon } from '@icons/RefreshIcon';
import { Counter } from '@/components/ui/counter';
import { PopOutList } from '../DataTable/PopOutList';
import { formatRelativeTime } from '../../utils/formatters';

/** One line of link text; the collapsed preview is exactly that tall. */
const LINK_LINE_HEIGHT = 15;
const POPOUT_WIDTH = 220;

interface AttioSyncStatusProps {
  /** Absent on the orders list, null while the attio_deal_sync flag is off. */
  attio?: AttioSync | null;
  orderId?: number;
  /** Gates Retry: without orders.write the button is hidden, not disabled. */
  currentUser?: CurrentUser;
  /**
   * Set once a pending sync has outlasted the poll budget. The row drops the
   * spinner but keeps the word: the sync is most likely still running, so
   * calling it an error would be wrong.
   */
  attioSyncTimedOut?: boolean;
}

/** Status key in en.yml, plus the badge tone. Failures share one label. */
const BADGE: Record<Exclude<AttioSyncState, 'pending'>, { key: string; tone: StatusTone }> = {
  not_synced: { key: 'not_synced', tone: 'neutral' },
  synced: { key: 'synced', tone: 'mint' },
  partially_synced: { key: 'partially_synced', tone: 'yellow' },
  closed: { key: 'closed', tone: 'cinnamon' },
  failed_rejected: { key: 'failed', tone: 'red' },
  failed_unreachable: { key: 'failed', tone: 'red' },
  failed_access: { key: 'failed', tone: 'red' },
  failed: { key: 'failed', tone: 'red' },
};

/**
 * The message key under attio_card.messages. partially_synced is absent
 * because its line is a count, not a fixed sentence, and synced has no second
 * line at all. Rows written before the three-way failure split read as
 * unreachable, matching how the server treats them.
 */
const MESSAGE: Partial<Record<AttioSyncState, string>> = {
  not_synced: 'not_synced',
  closed: 'closed',
  failed_rejected: 'failed_rejected',
  failed_unreachable: 'failed_unreachable',
  failed_access: 'failed_access',
  failed: 'failed_unreachable',
};

export function isAttioFailure(state: AttioSyncState): boolean {
  return state.startsWith('failed');
}

/**
 * Whether the Retry button can do anything. Hidden on failed_access because a
 * rep cannot fix a token -- pressing it would fail identically twenty seconds
 * later, which reads as the product being broken twice. Hidden on
 * partially_synced because the multi-deal guard fires on every run, so a retry
 * changes nothing, and on closed because the deal is gone.
 */
export function canRetryAttioSync(state: AttioSyncState): boolean {
  return isAttioFailure(state) && state !== 'failed_access';
}

export function AttioSyncStatus({
  attio,
  orderId,
  currentUser,
  attioSyncTimedOut = false,
}: AttioSyncStatusProps) {
  const { t } = useTranslation();

  if (!attio) return null;

  const { state, deals, last_synced_at: lastSyncedAt, last_sync_error: lastSyncError } = attio;

  // Content only: this sits in the right column of the Total order yield row,
  // above the payment status, so the grid belongs to the row, not here.
  return (
    <div className="space-y-4">
        <div className="flex items-center gap-4">
          <h3 className="font-inter text-lg font-semibold leading-normal text-off-black">
            {t('attio_card.title')}
          </h3>

          {state === 'pending' ? (
            <span
              className="flex items-center gap-2 font-inter text-xs font-normal leading-tight text-grey"
              role="status"
            >
              {!attioSyncTimedOut && (
                <span
                  aria-hidden="true"
                  className="size-3 animate-spin rounded-full border border-grey-light border-t-grey"
                />
              )}
              {t('attio_card.syncing')}
            </span>
          ) : (
            <>
              <Badge variant="status" tone={BADGE[state].tone}>
                {t(`attio_card.statuses.${BADGE[state].key}`)}
              </Badge>
              {lastSyncedAt && (
                <span className="font-inter text-xs font-normal leading-tight text-grey">
                  {formatRelativeTime(lastSyncedAt)}
                </span>
              )}
            </>
          )}

          {orderId !== undefined && canRetryAttioSync(state) &&
            can(currentUser, CAPABILITIES.ORDERS_WRITE) && (
              <RetryAttioSyncButton orderId={orderId} />
            )}
        </div>

        {state !== 'pending' && (
          <AttioSyncDetail state={state} deals={deals} lastSyncError={lastSyncError} />
        )}
    </div>
  );
}

function AttioSyncDetail({
  state,
  deals,
  lastSyncError,
}: {
  state: Exclude<AttioSyncState, 'pending'>;
  deals: AttioSync['deals'];
  lastSyncError: string | null;
}) {
  const { t } = useTranslation();

  // A deleted deal has nothing to open, and no link is the whole point of
  // not_synced.
  const showsLink = deals.length > 0 && state !== 'closed' && state !== 'not_synced';
  const messageKey = MESSAGE[state];

  // Attio's own message beats our copy on a failure: it names the code the rep
  // needs (400 value_not_found), which no fixed sentence can.
  const message = isAttioFailure(state)
    ? (lastSyncError ?? t(`attio_card.messages.${messageKey}`))
    : state === 'partially_synced'
      ? t('attio_card.duplicate_deals_found', { count: deals.length })
      : messageKey
        ? t(`attio_card.messages.${messageKey}`)
        : null;

  if (!showsLink && !message) return null;

  return (
    <div className="flex flex-wrap items-baseline gap-4">
      {showsLink && <AttioDealLink deals={deals} />}
      {message && (
        <p
          className={`font-inter text-xs font-normal leading-tight ${
            isAttioFailure(state)
              ? 'text-chilli-red'
              : state === 'partially_synced'
                ? 'text-turmeric-yellow'
                : 'text-grey'
          }`}
        >
          {message}
        </p>
      )}
    </div>
  );
}

function AttioDealLink({ deals }: { deals: AttioSync['deals'] }) {
  const { t } = useTranslation();

  if (deals.length === 1) {
    return (
      <a
        className={cn('font-inter text-xs font-normal leading-tight text-off-black', UNDERLINE_STATIC)}
        href={deals[0].url}
        rel="noreferrer"
        target="_blank"
      >
        {t('attio_card.view_deal')}
      </a>
    );
  }

  // Several deals: the label goes plural and the list opens on hover, the same
  // component the orders table's Events column uses. Deals are numbered rather
  // than named -- titles live in Attio and are deliberately not copied here, so
  // naming them would mean a read on every page load.
  return (
    <PopOutList
      collapsedHeight={LINK_LINE_HEIGHT}
      previewFadeHeight="0px"
      width={POPOUT_WIDTH}
      collapsed={
        <span className={cn('font-inter text-xs font-normal leading-tight text-off-black', UNDERLINE_STATIC)}>
          {t('attio_card.view_deals')}
        </span>
      }
      header={
        <div className="flex items-center gap-1.5">
          <span className="font-inter text-sm font-semibold leading-[1.5] text-grey">
            {t('attio_card.view_deals')}
          </span>
          <Counter variant="soft" value={deals.length} />
        </div>
      }
      expanded={
        <div className="flex flex-col gap-2">
          {deals.map((dealRecord, index) => (
            <a
              className={cn('font-inter text-xs font-normal leading-tight text-off-black', UNDERLINE_STATIC)}
              href={dealRecord.url}
              key={dealRecord.id}
              rel="noreferrer"
              target="_blank"
            >
              {t('attio_card.deal_label', { number: index + 1 })}
            </a>
          ))}
        </div>
      }
    />
  );
}

/**
 * Puts every linked deal back to pending and queues the sync. The row then
 * renders the spinner from the returned order, and useOrder's poll takes over
 * -- so there is no separate button-loading treatment to design.
 */
function RetryAttioSyncButton({ orderId }: { orderId: number }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: () => retryAttioSync(orderId),
    onSuccess: (order) => {
      queryClient.setQueryData(ordersQueryKeys.detail(orderId), order);
    },
  });

  return (
    <Button disabled={isPending} onClick={() => mutate()} size="sm" variant="outline">
      <RefreshIcon aria-hidden="true" className="size-4" />
      {t('attio_card.retry')}
    </Button>
  );
}
